import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SectionCard } from './SectionCard'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar } from '@/components/ui/avatar'
import { useTechnicianRanking } from '@/hooks/useDashboard'
import { formatCurrency, formatPercent } from '@/lib/format'
import { useDashboardPeriod } from './DashboardPeriodContext'
import { cn } from '@/lib/utils'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left font-sans text-[10px] font-medium leading-3.5 tracking-normal whitespace-nowrap text-[#3d434e] uppercase'
const tdClass =
  'h-16 border-b border-[#e7e9ef] px-3 py-2 font-sans text-sm font-normal text-[#171a20]'

const AVATAR_GRADIENTS = [
  'linear-gradient(149.66deg, #ebf1fe 0%, #2e5ce6 100%)',
  'linear-gradient(149.66deg, #e2f5f3 0%, #0e8a7a 100%)',
  'linear-gradient(149.66deg, #f2ebfd 0%, #7b3bea 100%)',
  'linear-gradient(149.66deg, #fcf2dc 0%, #c87b08 100%)',
  'linear-gradient(149.66deg, #fdeaea 0%, #d02f2f 100%)',
]

function TechnicianActivityIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
    >
      <g transform='translate(0.665 0.665)'>
        <path
          d='M8.80088 3.8691C8.67873 3.99371 8.61031 4.16126 8.61031 4.33576C8.61031 4.51026 8.67873 4.67781 8.80088 4.80243L9.86755 5.8691C9.99217 5.99125 10.1597 6.05967 10.3342 6.05967C10.5087 6.05967 10.6763 5.99125 10.8009 5.8691L13.3142 3.35576C13.6494 4.09655 13.7509 4.92192 13.6052 5.72186C13.4594 6.5218 13.0734 7.25832 12.4984 7.83328C11.9234 8.40824 11.1869 8.79432 10.387 8.94007C9.58704 9.08582 8.76167 8.98432 8.02088 8.64909L3.41421 13.2558C3.149 13.521 2.78929 13.67 2.41421 13.67C2.03914 13.67 1.67943 13.521 1.41421 13.2558C1.149 12.9905 1 12.6308 1 12.2558C1 11.8807 1.149 11.521 1.41421 11.2558L6.02088 6.6491C5.68566 5.9083 5.58416 5.08294 5.72991 4.283C5.87566 3.48306 6.26174 2.74653 6.83669 2.17158C7.41165 1.59662 8.14818 1.21054 8.94812 1.06479C9.74806 0.919039 10.5734 1.02054 11.3142 1.35576L8.80088 3.8691Z'
          stroke='#FF5A1F'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
    </svg>
  )
}

function formatPeriodMonth(dateFrom: string, locale: string): string {
  const date = new Date(`${dateFrom}T12:00:00`)
  return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
}

function formatTableCurrency(value: number | null | undefined): string {
  return formatCurrency(value, 'EUR', 2)
}

function CountBadge({
  value,
  tone,
}: {
  value: number
  tone: 'positive' | 'info'
}) {
  return (
    <span
      className={cn(
        'inline-flex h-5.5 min-w-5.5 items-center justify-center rounded-lg px-1.5 font-sans text-xs font-medium leading-4',
        value === 0
          ? 'bg-[#f4f5f8] text-[#171a20]'
          : tone === 'positive'
            ? 'bg-[#d1fae5] text-[#065f46]'
            : 'bg-[#dbeafe] text-[#1d44b3]'
      )}
    >
      {value}
    </span>
  )
}

// Technician performance ranking (AC-2.2.6). Top 5 by total revenue desc.
export function TechnicianActivityTable() {
  const { t, i18n } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { dateFrom, dateTo } = useDashboardPeriod()
  const { data, isLoading, isError } = useTechnicianRanking({
    date_from: dateFrom,
    date_to: dateTo,
  })
  const rows = (data?.data ?? []).slice(0, 5)
  const periodMonth = formatPeriodMonth(dateFrom, i18n.language)

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className='space-y-0 border-t border-border'>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className='h-16 w-full rounded-none' />
        ))}
      </div>
    )
  } else if (isError) {
    body = (
      <p className='py-8 text-center text-sm text-destructive'>
        {t('technicianActivity.unableToLoad')}
      </p>
    )
  } else if (rows.length === 0) {
    body = (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        {t('technicianActivity.empty')}
      </p>
    )
  } else {
    body = (
      <Table className='min-w-250'>
        <TableHeader>
          <TableRow className='border-0'>
            <TableHead className={cn(thClass, 'w-93')}>
              {t('technicianActivity.columns.technician')}
            </TableHead>
            <TableHead className={cn(thClass, 'w-35 text-center')}>
              {t('technicianActivity.columns.completed')}
            </TableHead>
            <TableHead className={cn(thClass, 'w-35 text-center')}>
              {t('technicianActivity.columns.inProgress')}
            </TableHead>
            <TableHead className={cn(thClass, 'w-35 text-center')}>
              {t('technicianActivity.columns.planned')}
            </TableHead>
            <TableHead className={cn(thClass, 'w-55')}>
              {t('technicianActivity.columns.completionRate')}
            </TableHead>
            <TableHead className={cn(thClass, 'w-50 text-center')}>
              {t('technicianActivity.columns.avgRevenue')}
            </TableHead>
            <TableHead className={cn(thClass, 'w-50 text-center')}>
              {t('technicianActivity.columns.totalRevenue')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, index) => {
            const rate = r.completed_rate ?? null
            return (
              <TableRow
                key={r.technician_id}
                className='cursor-pointer border-0'
                onClick={() => navigate(`/technicians/${r.technician_id}`)}
              >
                <TableCell className={tdClass}>
                  <div className='flex min-w-0 items-center gap-2'>
                    <Avatar
                      name={r.full_name}
                      src={r.avatar_url}
                      className='size-8 bg-transparent font-heading text-sm font-semibold text-white'
                      style={
                        r.avatar_url
                          ? undefined
                          : {
                              backgroundImage:
                                AVATAR_GRADIENTS[
                                  index % AVATAR_GRADIENTS.length
                                ],
                            }
                      }
                    />
                    <span className='truncate font-sans text-sm font-medium leading-[1.4]'>
                      {r.full_name}
                    </span>
                  </div>
                </TableCell>
                <TableCell className={cn(tdClass, 'text-center')}>
                  <CountBadge value={r.completed_jobs} tone='positive' />
                </TableCell>
                <TableCell className={cn(tdClass, 'text-center')}>
                  <CountBadge value={r.in_progress_jobs ?? 0} tone='info' />
                </TableCell>
                <TableCell className={cn(tdClass, 'text-center')}>
                  <CountBadge value={r.not_started_jobs ?? 0} tone='info' />
                </TableCell>
                <TableCell className={cn(tdClass, 'pr-12')}>
                  <div className='flex items-center gap-2'>
                    <Progress
                      value={rate ?? 0}
                      className='h-2 bg-[#f4f5f8]'
                      indicatorClassName='bg-[#10b981]'
                    />
                    <span className='min-w-10 shrink-0 font-sans text-sm font-medium tabular-nums'>
                      {formatPercent(rate)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className={cn(tdClass, 'text-center')}>
                  {r.avg_revenue_per_intervention != null
                    ? formatTableCurrency(r.avg_revenue_per_intervention)
                    : '-'}
                </TableCell>
                <TableCell className={cn(tdClass, 'text-center')}>
                  <span className='font-medium'>
                    {formatTableCurrency(r.total_revenue)}
                  </span>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    )
  }

  return (
    <SectionCard
      title={t('technicianActivity.title')}
      variant='chart'
      contentClassName='px-6 pt-4 pb-6 [&>div]:scrollbar-none [&>div]:overflow-x-auto [&>div]:overflow-y-hidden'
      headerIcon={<TechnicianActivityIcon />}
      headerIconClassName='bg-[#fff1ea]'
      headerRight={periodMonth}
    >
      {body}
    </SectionCard>
  )
}

export default TechnicianActivityTable
